import HTTPService from './HTTPService';
import GradeRequest from '../models/requests/GradeRequest';
import ApprenticeOverviewResponse from '../models/responses/ApprenticeOverviewResponse';
import SubjectResponse from '../models/responses/SubjectResponse';
import GradeResponse from '../models/responses/GradeResponse';
import DashboardResponse from '../models/responses/DashboardResponse';
import UserResponse from '../models/responses/UserResponse';
import SemesterResponse from '../models/responses/SemesterResponse';
import ApprenticeResponse from '../models/responses/ApprenticeResponse';
import SubjectDetailResponse from '../models/responses/SubjectDetailResponse';
import store from '../store';
import { i18n } from '../main';

export default {
  /**
   * Gets information about the currently logged in user.
   *
   * @returns current user information
   */
  async getMyUser(): Promise<UserResponse> {
    return await HTTPService.get('users/me').then((res) => res.data);
  },

  /**
   * Gets all subjects by semester id or by default it gets the newest semester
   *
   * @param semesterId - optional parameter which defines what semester is selected.
   * @returns dashboard information
   */
  getDashboard(semesterId?: string): Promise<DashboardResponse> {
    return new Promise((resolve, reject) => {
      HTTPService.get(
        `dashboard${semesterId ? `?semesterId=${semesterId}` : ''}`,
      )
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Creates a grade.
   *
   * @param subjectId - id of subject which will hold the new grade
   * @param grade - the grade information to save in the database
   * @returns the created grade
   */
  createGrade(subjectId: string, grade: GradeRequest): Promise<GradeResponse> {
    const url = '/subjects/' + subjectId + '/grades';
    return new Promise((resolve, reject) => {
      HTTPService.post(url, grade)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.created',
            text: i18n.tc('subjectDetail.gradeCard.grade'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Updates a grade by id.
   *
   * @param grade - the updated grade information
   * @returns the updated grade
   */
  updateGrade(gradeId: string, grade: GradeRequest): Promise<GradeResponse> {
    return new Promise((resolve, reject) => {
      HTTPService.put('/grades/' + gradeId, grade)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.updated',
            text: i18n.tc('subjectDetail.gradeCard.grade'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Deletes grade by id.
   *
   * @param gradeId - id of grade to delete
   */
  deleteGrade(gradeId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      HTTPService.delete('/grades/' + gradeId)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.deleted',
            text: i18n.tc('subjectDetail.gradeCard.grade'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Creates a semester
   *
   * @param name - name of new semester
   * @returns the created semester
   */
  createSemester(name: string | null): Promise<SemesterResponse> {
    const data = { name };
    return new Promise((resolve, reject) => {
      HTTPService.post('/semesters/', data)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.created',
            text: i18n.tc('apprenticeDashboard.semesterTitle'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Updates a semester by id
   *
   * @param semesterId - id of semester to update
   * @param name - updated name
   * @returns the updated semester
   */
  updateSemester(
    semesterId: string,
    name: string | null,
  ): Promise<SemesterResponse> {
    const data = { name };
    return new Promise((resolve, reject) => {
      HTTPService.put('/semesters/' + semesterId, data)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.updated',
            text: i18n.tc('apprenticeDashboard.semesterTitle'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Deletes a semester by id
   *
   * @param semesterId - id of semester to delete
   */
  deleteSemester(semesterId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      HTTPService.delete('/semesters/' + semesterId)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.deleted',
            text: i18n.tc('apprenticeDashboard.semesterTitle'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Fetches detail information of a single subject.
   *
   * @param subjectId - id of subject
   * @returns detail information of subject
   */
  getSubject(subjectId: string): Promise<SubjectDetailResponse> {
    return HTTPService.get(`/subjects/${subjectId}`).then((res) => res.data);
  },

  /**
   * Creates a subject
   *
   * @param semesterId - id of semester which will hold the new subject
   * @param name - name of the new subject
   * @returns the created subject
   */
  createSubject(
    semesterId: string,
    name: string | null,
  ): Promise<SemesterResponse> {
    const data = { name };
    return new Promise((resolve, reject) => {
      HTTPService.post('/semesters/' + semesterId + '/subjects', data)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.created',
            text: i18n.tc('apprenticeDashboard.subject'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Updates a subject by id.
   *
   * @param subjectId - id of subject to update
   * @param name - update name of subject
   * @returns the updated subject
   */
  updateSubject(
    subjectId: string,
    name: string | null,
  ): Promise<SubjectResponse> {
    const data = { name };
    return new Promise((resolve, reject) => {
      HTTPService.put('/subjects/' + subjectId, data)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.updated',
            text: i18n.tc('apprenticeDashboard.subject'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Deletes subject by id
   *
   * @param subjectId - id of subject to delete
   */
  deleteSubject(subjectId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      HTTPService.delete('/subjects/' + subjectId)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'general.deleted',
            text: i18n.tc('apprenticeDashboard.subject'),
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Gets all apprentices of coach.
   *
   * @returns list of apprentices
   */
  getApprentices(): Promise<Array<ApprenticeResponse>> {
    return new Promise((resolve, reject) => {
      HTTPService.get('/apprentices')
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Gets all subjects by semester id or by default it gets the newest semester from an apprentice
   *
   * @param semesterId - optional id which defines the selected semester
   * @returns apprentice detail information
   */
  getApprenticeDetail(
    apprenticeId: string,
    semesterId?: string,
  ): Promise<ApprenticeOverviewResponse> {
    return new Promise((resolve, reject) => {
      HTTPService.get(
        `/apprentices/${apprenticeId}/overview${
          semesterId ? `?semesterId=${semesterId}` : ''
        }`,
      )
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => reject(err.message));
    });
  },

  /**
   * Removes Apprentice from Coach
   *
   * @param apprenticeId - id of apprentice which should be removed
   */
  removeApprentice(apprenticeId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      HTTPService.delete(`/apprentices/${apprenticeId}/coach`)
        .then((res) => {
          resolve(res.data);
          store.dispatch('showNotification', {
            key: 'apprenticeDetail.removeApprenticeNotification',
            type: 'confirmation',
          });
        })
        .catch((err) => reject(err.message));
    });
  },
};
